/**
 * **Post-attack lifecycle observers** — the Category-A *observer* seam at the
 * **`on_attack_made`** point: the third sibling of the post-move chain
 * (`spatialRecompute.ts` / `MOVE_LISTENER_RECOMPUTERS`/`SETTLE_RECOMPUTERS`) and the turn-end chain
 * (`turn_ended`'s rules in `referee/refereeCommand.ts`).
 *
 * **The point exists because a consumer needed it.** A trigger point with no consumer is
 * speculative rot, so `on_attack_made` was deliberately held back until the reveal-on-attack
 * consumer below justified it — hold any further point to the same bar.
 *
 * **The firing point.** Each observer is one rule of `attack_resolved` (`ATTACK_RESOLVED_RULES`,
 * `referee/refereeIntent.ts`, `event-engine-lifecycle` 2.4), raised **after** an attack/forced-save
 * resolution commits (its own `apply_resolved` already applied + broadcast),
 * gated on the resolution actually being an attack or a forced save (a `to_hit`/`save` step is
 * present) so a non-attacking activation never spuriously clears Hidden. Each observer's steps
 * apply as their **own** eager-logged `apply_resolved` (the observer's `kind` label) — a
 * reified, invertible, deterministically-ordered grounded step, exactly like the post-move /
 * turn-end observers. NOT an EventEmitter, not a mutating listener, not bespoke inline goo.
 *
 * **Category A only.** A post-attack observer fires-and-forgets: it never pauses resolution to
 * ask a human — that is a *reaction*, a separate mechanism.
 */

import { type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import { clearHiddenSteps, revealExposedHiddenSteps } from "./hide";
import { grappleImmobilizedSyncSteps } from "./grapple";

/** The firing context handed to each post-attack observer. */
export type AttackContext = {
  /** The authoritative state **after** the attack/forced-save resolution has been applied. */
  state: EncounterState;
  /** The combatant that made the attack / forced the save (the resolution's source/initiator). */
  attacker: string;
};

/**
 * One post-attack lifecycle observer: a pure function from the post-resolution state + the
 * attacking combatant to the grounded steps that bring one consequence in sync. `id` is the
 * stable identity the chain orders by; `kind` is the `apply_resolved` retained-log label the
 * room stamps on this observer's resolution (each observer's steps land under a distinct,
 * auditable tag — exactly like the post-move `spatial_${id}` / turn-end observer tags).
 */
export type AttackObserver = {
  /** Stable id — the chain runs fixed by this list, never by registration order. */
  readonly id: string;
  /** The `apply_resolved` kind label this observer's resolution carries (retained-log tag). */
  readonly kind: string;
  /** Pure: read the post-attack state + attacker, return the consequence steps. Never mutates. */
  observe(ctx: AttackContext): ResolvedStep[];
};

/**
 * **Reveal-on-attack observer.** RAW: a Hidden character that makes an attack or forces a save
 * reveals itself (clears Hidden). If the attacker is Hidden, emit the `status_remove` clear
 * step; otherwise nothing (idempotent — a non-Hidden attacker reveals to zero steps, so the
 * observer is a no-op).
 */
export const revealOnAttackObserver: AttackObserver = {
  id: "reveal_on_attack",
  kind: "reveal_hidden",
  observe({ state, attacker }: AttackContext): ResolvedStep[] {
    return clearHiddenSteps(state.combatants[attacker]);
  },
};

/**
 * **Cover-destroyed reveal sweep** — the third Hide reveal trigger ({@link revealOnAttackObserver}
 * and the turn-end `revealExposedAtTurnEndSteps` in `hide.ts` are the other two). RAW: HIDE assumes
 * cover can be destroyed — "you lose HIDDEN if your cover disappears or is destroyed" — so an
 * attack that blows up a Hidden unit's cover must re-check that unit's Hide, not just the
 * attacker's.
 *
 * **The body is the shared {@link revealExposedHiddenSteps} sweep** — a full sweep over every
 * Hidden unit, not just the attacker, re-running `checkHideCover` and clearing whichever units the
 * current board now exposes. This observer and the post-move `hiddenRevealRecomputer`
 * (`spatialRecompute.ts`) share that one sweep body but keep distinct identities, so each keeps
 * its own audit trail. Idempotent — a sweep that changes nothing emits zero steps (no broadcast),
 * and a unit whose Hidden the sibling {@link revealOnAttackObserver} already cleared this same
 * chain-run simply reads as not-Hidden here (a no-op, not a double-clear).
 *
 * **Why this needs no bespoke destruction→spatial-recompute plumbing:** cover/LoS is a live query
 * (`terrainVisibility`, which already folds object contributions) — once an object hits 0 HP and
 * gets its `Destroyed` marker, the very next `checkHideCover` call already sees it as gone. This
 * observer is only the missing *trigger* (something has to re-invoke the check after an attack),
 * not a new geometry.
 */
export const coverDestroyedObserver: AttackObserver = {
  id: "cover_destroyed",
  kind: "reveal_hidden",
  observe({ state }: AttackContext): ResolvedStep[] {
    return revealExposedHiddenSteps(state);
  },
};

/**
 * **Grapple dominance/Immobilized sync observer** — catches the `act_grapple` on-hit bind: after
 * an attack resolution commits (the on-hit trigger has already stamped both parties' `Grappled`
 * marker), the full-sweep sync stamps a partner-provenance `Immobilized` on the smaller/
 * non-dominant party. Ignores `attacker` — a full sweep, same posture as
 * {@link coverDestroyedObserver}, since an on-hit bind can newly justify (or, via a same-Size
 * dominance flip, un-justify) dominance for a pair the attack didn't directly touch.
 */
export const grappleDominanceObserver: AttackObserver = {
  id: "grapple_dominance",
  kind: "grapple_dominance",
  observe({ state }: AttackContext): ResolvedStep[] {
    return grappleImmobilizedSyncSteps(state);
  },
};
